import SlideFrame from "@/components/SlideFrame";
import { ClipboardList, Sparkles, Handshake, ArrowRight, Calendar, ExternalLink } from "lucide-react";
import { packFor } from "@/lib/industryPack";

/* ============================ 04 · How It Works ============================ */
export function S_HowItWorks({ idx, total, prospect }) {
  const pack = packFor(prospect);
  const corp = pack.corporate;
  const overrides = pack.howItWorksSteps || {};

  const lf = pack.lawFirm;
  const isBankFintechMature = pack.kind === "1A" || pack.kind === "3" || pack.kind === "6";

  // Onboarding copy: banks / fintechs > 3 yrs old get the broader "enterprise" onboarding.
  // Newer / smaller / non-regulated teams can self-manage via the free guardian link.
  const onboardingDefault = lf
    ? "Fill out our short onboarding form. We review your matter intake, current DMS, AI policy, and client communication workflows — and surface where the firm is exposed."
    : isBankFintechMature
      ? "For established banks and fintechs (3+ years), we run a broader enterprise onboarding — chartering structure, sponsor-bank relationships, current program docs, and a real gap analysis against your regulators."
      : "Newer teams can kick the tires for free via the self-managed review at complisunguardian.com. Larger / regulated orgs get a full enterprise onboarding and gap analysis from our team.";

  const steps = [
    {
      n: "01",
      icon: ClipboardList,
      t: "Onboarding + Gap Analysis",
      d: overrides.s1body || onboardingDefault,
      cta: lf ? null : (isBankFintechMature ? null : { href: "https://complisunguardian.com", label: "Self-managed: complisunguardian.com" }),
    },
    {
      n: "02",
      icon: Sparkles,
      t: "We Build the Prototype",
      d: overrides.s2body || "Within 14 days you get a working prototype — your branding, your policies, your calendar, your team. We refine it with you until it fits.",
    },
    {
      n: "03",
      icon: Handshake,
      t: "Go Live + Keep Scaling",
      d: overrides.s3body || "You go live — and we stay alongside you. The fractional team scales up or down with your business (busy quarter? bigger team. quiet stretch? lighter touch). This isn't a hand-off, it's a long-term partnership.",
    },
  ];

  return (
    <SlideFrame idx={idx} total={total} kicker="How It Works" dark>
      <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-5">/ from problem to fully-functional in 45 days</p>
      <h2 className="serif font-light text-5xl md:text-6xl leading-[1.04] tracking-tight max-w-4xl">
        Three steps. <em className="italic text-sun-600">Forty-five days.</em>
      </h2>

      <div className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-4 stagger">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.n}
                 data-testid={`howitworks-step-${s.n}`}
                 className="border border-ink-900/12 rounded-md p-5 bg-white/70 hover:bg-white transition-colors flex flex-col">
              <div className="flex items-center gap-2">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">{s.n}</div>
                <Icon className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
              </div>
              <div className="serif text-xl text-ink-900 mt-3 leading-tight">{s.t}</div>
              <div className="text-sm text-ink-900/70 mt-2 leading-relaxed flex-1">{s.d}</div>
              {s.cta && (
                <a href={s.cta.href} target="_blank" rel="noreferrer"
                   className="mt-3 inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.22em] text-ink-900 hover:text-sun-700 transition-colors border border-ink-900/12 rounded px-2.5 py-1.5 w-fit">
                  {s.cta.label} <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="mt-9 border border-ink-900/12 rounded-md p-6 bg-white/60" data-testid="howitworks-timeline">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">45-Day Timeline</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Phase n="Days 1–5"   t="Discover & Plan"           d={overrides.p1body || "Onboarding form, gap analysis, regulators mapped, scope agreed."} />
          <Phase n="Days 6–14"  t="Prototype + Sign-off"      d={overrides.p2body || "Live prototype with your branding + workflows in 14 days. You walk it, request adjustments, we iterate until it fits."} />
          <Phase n="Days 15–45" t={(corp || lf) ? "Refine, Go Live, Keep Scaling" : "Go Live + Ongoing Partnership"} d={overrides.p3body || "Refinements baked in; you go live; fractional team scales up or down with your business — not a hand-off."} />
        </div>
        <div className="mt-5 flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-sun-700">
          <ArrowRight className="h-3 w-3" />
          {overrides.timelineCallout || "$5,000 installation covers all 45 days of prototyping, compliance, and operational support"}
        </div>
      </div>
    </SlideFrame>
  );
}

function Phase({ n, t, d }) {
  return (
    <div className="border-l-2 border-sun-500 pl-3">
      <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700">{n}</div>
      <div className="serif text-base text-ink-900 leading-tight mt-0.5">{t}</div>
      <div className="text-xs text-ink-900/65 mt-1 leading-relaxed">{d}</div>
    </div>
  );
}
